import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { signup } from "../api/authApi";
import { Loader2, Check, Building2, User, Mail, Lock } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { Plan } from "../types";

const PLANS: Plan[] = [
  {
    id: "lite",
    name: "Lite",
    price: 25,
    description: "Basic starter plan",
    features: ["3 users", "50 quotes/month", "30 jobs/month", "Basic support"],
  },
  {
    id: "core",
    name: "Core",
    price: 80,
    description: "⭐ Most Popular – Recommended",
    features: ["7 users", "150 quotes/month", "150 jobs/month", "Email support"],
    recommended: true,
  },
  {
    id: "pro",
    name: "Pro",
    price: 150,
    description: "Advanced features & AI",
    features: ["15 users", "Unlimited quotes & jobs", "AI automations", "Priority support"],
  },
  {
    id: "unlimited",
    name: "Unlimited",
    price: 250,
    description: "Enterprise-grade solution",
    features: ["Unlimited users", "Unlimited everything", "AI automations", "24/7 support"],
  },
];

export default function Signup() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    businessName: "",
    ownerName: "",
    ownerEmail: "",
    password: "",
    confirmPassword: "",
    selectedPlan: "core",
    agreedToTerms: false,
    agreedToPrivacy: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.businessName.trim()) {
      newErrors.businessName = "Business name is required";
    }

    if (!formData.ownerName.trim()) {
      newErrors.ownerName = "Your name is required";
    }

    if (!formData.ownerEmail.trim()) {
      newErrors.ownerEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) {
      newErrors.ownerEmail = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = "You must agree to the Terms & Conditions";
    }

    if (!formData.agreedToPrivacy) {
      newErrors.agreedToPrivacy = "You must agree to the Privacy Policy";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await signup({
        businessName: formData.businessName,
        ownerName: formData.ownerName,
        ownerEmail: formData.ownerEmail,
        password: formData.password,
        selectedPlan: formData.selectedPlan,
      });

      // Redirect to Stripe checkout if checkoutUrl is provided
      if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
        return;
      }

      // Account created but no checkout URL (shouldn't happen in normal flow)
      toast({
        title: "Account created!",
        description: "Welcome to TradeReady. Redirecting to onboarding...",
      });

      // Redirect to onboarding using window.location to ensure clean navigation
      // This prevents React hook initialization issues during navigation
      setTimeout(() => {
        window.location.href = "/onboarding/welcome";
      }, 500);
    } catch (error: any) {
      // Check if account was created but payment setup failed
      if (error.accountCreated) {
        toast({
          title: "Account created",
          description: error.message || "Account created successfully, but payment setup failed. Please log in to complete payment.",
          variant: "default",
        });
        
        // Redirect to login after brief delay using window.location for clean navigation
        setTimeout(() => {
          window.location.href = "/login";
        }, 3000);
      } else {
        // Complete signup failure
        toast({
          title: "Signup failed",
          description: error.message || "Could not create account. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 text-white relative overflow-hidden">
      {/* Decorative background orbs */}
      <div className="absolute -top-24 -left-24 w-[450px] h-[450px] bg-blue-400 rounded-full blur-3xl opacity-30 animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-[520px] h-[520px] bg-indigo-400 rounded-full blur-3xl opacity-20"></div>

      {/* Header */}
      <header className="flex items-center justify-between px-8 py-6 z-10">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center p-1.5">
            <img src="/images/logo.png" alt="TradeReady" className="h-full w-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="heading-app-name">
            TradeReady
          </h1>
        </div>
        <p className="text-sm text-blue-100 hidden sm:block">Powering Trade Businesses Everywhere</p>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-8 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl w-full bg-white/10 backdrop-blur-md rounded-3xl p-8 sm:p-10 shadow-2xl border border-white/20 my-8"
          data-testid="card-signup"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-3 text-white" data-testid="heading-create-account">
              Create Your Account
            </h2>
            <p className="text-blue-100 text-sm">
              Start managing your jobs, quotes & payments with TradeReady
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="form-signup">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="businessName" className="block text-sm font-medium mb-2 text-blue-100" data-testid="label-business-name">
                  Business Name
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="businessName"
                    data-testid="input-business-name"
                    type="text"
                    placeholder="Smith Plumbing"
                    value={formData.businessName}
                    onChange={(e) =>
                      setFormData({ ...formData, businessName: e.target.value })
                    }
                    className="pl-10 bg-white/90 text-gray-800 border-gray-200 focus:ring-2 focus:ring-blue-400 dark:bg-white/90 dark:text-gray-800"
                    disabled={isLoading}
                  />
                </div>
                {errors.businessName && (
                  <p className="text-sm text-red-200 mt-1" data-testid="error-business-name">
                    {errors.businessName}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="ownerName" className="block text-sm font-medium mb-2 text-blue-100" data-testid="label-owner-name">
                  Your Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="ownerName"
                    data-testid="input-owner-name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.ownerName}
                    onChange={(e) =>
                      setFormData({ ...formData, ownerName: e.target.value })
                    }
                    className="pl-10 bg-white/90 text-gray-800 border-gray-200 focus:ring-2 focus:ring-blue-400 dark:bg-white/90 dark:text-gray-800"
                    disabled={isLoading}
                  />
                </div>
                {errors.ownerName && (
                  <p className="text-sm text-red-200 mt-1" data-testid="error-owner-name">
                    {errors.ownerName}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="ownerEmail" className="block text-sm font-medium mb-2 text-blue-100" data-testid="label-owner-email">
                Business Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="ownerEmail"
                  data-testid="input-owner-email"
                  type="email"
                  placeholder="john@smithplumbing.com"
                  value={formData.ownerEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, ownerEmail: e.target.value })
                  }
                  className="pl-10 bg-white/90 text-gray-800 border-gray-200 focus:ring-2 focus:ring-blue-400 dark:bg-white/90 dark:text-gray-800"
                  disabled={isLoading}
                />
              </div>
              {errors.ownerEmail && (
                <p className="text-sm text-red-200 mt-1" data-testid="error-owner-email">
                  {errors.ownerEmail}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password" className="block text-sm font-medium mb-2 text-blue-100" data-testid="label-password">
                  Password
                </Label>
                <PasswordInput
                  id="password"
                  data-testid="input-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="bg-white/90 text-gray-800 border-gray-200 focus:ring-2 focus:ring-blue-400 dark:bg-white/90 dark:text-gray-800"
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4 text-gray-400" />}
                />
                {errors.password && (
                  <p className="text-sm text-red-200 mt-1" data-testid="error-password">
                    {errors.password}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="block text-sm font-medium mb-2 text-blue-100" data-testid="label-confirm-password">
                  Confirm Password
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  data-testid="input-confirm-password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({ ...formData, confirmPassword: e.target.value })
                  }
                  className="bg-white/90 text-gray-800 border-gray-200 focus:ring-2 focus:ring-blue-400 dark:bg-white/90 dark:text-gray-800"
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4 text-gray-400" />}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-red-200 mt-1" data-testid="error-confirm-password">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="block text-sm font-medium text-blue-100" data-testid="label-plan-selection">
                Choose Your Plan
              </Label>
              <RadioGroup
                value={formData.selectedPlan}
                onValueChange={(value) => {
                  if (!isLoading) {
                    setFormData({ ...formData, selectedPlan: value });
                }
                }}
                disabled={isLoading}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {PLANS.map((plan) => (
                  <Label
                    key={plan.id}
                    htmlFor={`plan-${plan.id}`}
                    className={`relative flex flex-col rounded-xl border-2 p-4 transition-all cursor-pointer ${
                      formData.selectedPlan === plan.id
                        ? "border-white bg-white/20"
                        : "border-white/30 bg-white/5 hover:bg-white/10"
                    }`}
                    data-testid={`plan-option-${plan.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <RadioGroupItem
                        value={plan.id}
                        id={`plan-${plan.id}`}
                        className="mt-1 border-white text-white"
                      />
                      <div className="text-right">
                        <div className="font-bold text-xl text-white">${plan.price}</div>
                        <div className="text-xs text-blue-100">/month</div>
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-white text-lg">{plan.name}</span>
                        {plan.recommended && (
                          <Badge variant="default" className="text-xs bg-indigo-500 text-white" data-testid="badge-recommended">
                            Popular
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-blue-100 mb-3">
                        {plan.description}
                      </div>
                      <ul className="space-y-1.5">
                        {plan.features.map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-center text-sm text-blue-50"
                          >
                            <Check className="h-3.5 w-3.5 mr-2 text-white flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    </Label>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms-agreement"
                    checked={formData.agreedToTerms}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, agreedToTerms: checked as boolean })
                    }
                    disabled={isLoading}
                    data-testid="checkbox-terms"
                    className="mt-1 border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-blue-600"
                  />
                  <Label htmlFor="terms-agreement" className="text-sm text-blue-100 leading-relaxed cursor-pointer">
                    I agree to the{" "}
                    <a
                      href="/legal/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-white font-medium hover:text-blue-100"
                      data-testid="link-terms"
                    >
                      TradeReady Terms & Conditions
                    </a>
                  </Label>
                </div>
                {errors.agreedToTerms && (
                  <p className="text-sm text-red-200 ml-8" data-testid="error-terms">
                    {errors.agreedToTerms}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy-agreement"
                    checked={formData.agreedToPrivacy}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, agreedToPrivacy: checked as boolean })
                    }
                    disabled={isLoading}
                    data-testid="checkbox-privacy"
                    className="mt-1 border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-blue-600"
                  />
                  <Label htmlFor="privacy-agreement" className="text-sm text-blue-100 leading-relaxed cursor-pointer">
                    I agree to the{" "}
                    <a
                      href="/legal/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-white font-medium hover:text-blue-100"
                      data-testid="link-privacy"
                    >
                      TradeReady Privacy Policy
                    </a>
                  </Label>
                </div>
                {errors.agreedToPrivacy && (
                  <p className="text-sm text-red-200 ml-8" data-testid="error-privacy">
                    {errors.agreedToPrivacy}
                  </p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-semibold shadow-lg"
              disabled={isLoading}
              data-testid="button-signup"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>

            <div className="text-center text-sm text-blue-100">
              <p>
                Already have an account?{" "}
                <a 
                  href="/login" 
                  className="underline text-white font-medium hover:text-blue-100"
                  data-testid="link-login"
                >
                  Sign in
                </a>
              </p>
            </div>
          </form>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-blue-100 z-10">
        <p>© {new Date().getFullYear()} TradeReady • Built for Trades • All Rights Reserved</p>
        <p className="mt-2 space-x-3">
          <Link href="/legal/terms" data-testid="footer-link-terms">
            <span className="underline hover:text-white cursor-pointer">Terms</span>
          </Link>
          <span>•</span>
          <Link href="/legal/privacy" data-testid="footer-link-privacy">
            <span className="underline hover:text-white cursor-pointer">Privacy</span>
          </Link>
          <span>•</span>
          <Link href="/legal/dpa" data-testid="footer-link-dpa">
            <span className="underline hover:text-white cursor-pointer">DPA</span>
          </Link>
          <span>•</span>
          <Link href="/legal/sla" data-testid="footer-link-sla">
            <span className="underline hover:text-white cursor-pointer">SLA</span>
          </Link>
        </p>
      </footer>
    </div>
  );
}

